import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Store, Plus, Users, Mail, Edit, AlertCircle, CreditCard, CheckCircle, BarChart3, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import PayPalPayment from '../components/PayPalPayment';
import VideoCall from '../components/VideoCall';
import MessagingPanel from '../components/MessagingPanel';
import { useCurrency } from '../contexts/CurrencyContext';
import MultiLanguageInput from '../components/MultiLanguageInput';
import { useTranslation } from 'react-i18next';
import SellerOverviewTab from '../components/seller/SellerOverviewTab';
import SellerMediaTab from '../components/seller/SellerMediaTab';
import SellerRepsTab from '../components/seller/SellerRepsTab';
import SellerProductsTab from '../components/seller/SellerProductsTab';
import SellerInteractionsTab from '../components/seller/SellerInteractionsTab';
import SellerStatsTab from '../components/seller/SellerStatsTab';

const TIER_BENEFITS = {
  basic: { nameKey: 'dashboard.tier_basic', price: 500, color: 'bg-slate-100 text-slate-700', benefitKeys: ['dashboard.benefit_virtual_booth', 'dashboard.benefit_access_attendees', 'dashboard.benefit_basic_listing'] },
  premium: { nameKey: 'dashboard.tier_premium', price: 1500, color: 'bg-orange-100 text-orange-700', benefitKeys: ['dashboard.benefit_everything_basic', 'dashboard.benefit_booth_visitors', 'dashboard.benefit_priority_support', 'dashboard.benefit_enhanced_listing'] },
  elite: { nameKey: 'dashboard.tier_elite', price: 3000, color: 'bg-purple-100 text-purple-700', benefitKeys: ['dashboard.benefit_everything_premium', 'dashboard.benefit_all_attendees', 'dashboard.benefit_banner_promotion', 'dashboard.benefit_video_help', 'dashboard.benefit_promo_materials'] }
};

const SellerDashboard = () => {
  const { user } = useContext(AuthContext);
  const { formatPrice, getCurrencyCode } = useCurrency();
  const { t, i18n } = useTranslation();

  // Core data
  const [events, setEvents] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [myBooths, setMyBooths] = useState([]);
  const [activeBooth, setActiveBooth] = useState(null);
  const [allReps, setAllReps] = useState([]);
  const [allInteractions, setAllInteractions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [reps, setReps] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [showBoothModal, setShowBoothModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);

  // Form states
  const [pendingBooth, setPendingBooth] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTier, setSelectedTier] = useState('basic');
  const [activeVideoRoom, setActiveVideoRoom] = useState(null);
  const [callStats, setCallStats] = useState(null);
  const [boothVisitors, setBoothVisitors] = useState(null);
  const [eventAttendees, setEventAttendees] = useState(null);
  const [listPricing, setListPricing] = useState(null);
  const [purchasingList, setPurchasingList] = useState(null);
  const [notificationCounts, setNotificationCounts] = useState({ messages: 0, queue: 0, requests: 0 });
  const [boothForm, setBoothForm] = useState({ company_name: '', description: '', description_translations: {}, slogan_translations: {}, logo_url: '', banner_url: '', slogan: '' });
  const [profileForm, setProfileForm] = useState({ name: '', company: '', phone: '', company_email: '', website: '', street_address: '', city: '', state_province: '', postal_code: '', country: '', slogan: '', description: '' });

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  useEffect(() => { fetchData(); }, []);

  // Polling for real-time notifications
  useEffect(() => {
    if (!user || myBooths.length === 0) return;
    const pollForUpdates = async () => {
      try {
        const [countsRes, ...interactionsResults] = await Promise.all([
          axios.get(`${API}/notifications/counts`).catch(() => ({ data: { messages: 0, queue: 0, requests: 0 } })),
          ...myBooths.map(b => axios.get(`${API}/interactions/booth/${b.id}`).catch(() => ({ data: [] })))
        ]);
        const newAllInteractions = interactionsResults.flatMap(i => i.data);
        const oldPendingCount = allInteractions.filter(i => i.status === 'pending').length;
        const newPendingCount = newAllInteractions.filter(i => i.status === 'pending').length;
        if (newPendingCount > oldPendingCount) {
          toast.info(`${newPendingCount - oldPendingCount} new request(s) received!`, { description: 'Check the Interactions tab', duration: 5000 });
        }
        setAllInteractions(newAllInteractions);
        setNotificationCounts(countsRes.data);
        if (activeBooth) setInteractions(newAllInteractions.filter(i => i.booth_id === activeBooth.id));
      } catch (error) { console.error('Polling error:', error); }
    };
    const pollInterval = setInterval(pollForUpdates, 15000);
    return () => clearInterval(pollInterval);
  }, [user, myBooths.length, activeBooth?.id, allInteractions.length]);

  const fetchData = async () => {
    try {
      const [eventsRes, boothsRes, unreadRes, countsRes] = await Promise.all([
        axios.get(`${API}/events`), axios.get(`${API}/booths`),
        axios.get(`${API}/messages/unread-count`).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/notifications/counts`).catch(() => ({ data: { messages: 0, queue: 0, requests: 0 } }))
      ]);
      setEvents(eventsRes.data);
      setLiveEvents(eventsRes.data.filter(e => e.status === 'live' || e.status === 'registration_open'));
      const userBooths = boothsRes.data.filter(b => b.seller_id === user?.id);
      setMyBooths(userBooths);
      if (userBooths.length > 0) {
        if (activeBooth) {
          const updated = userBooths.find(b => b.id === activeBooth.id);
          if (updated) setActiveBooth(updated);
        } else setActiveBooth(userBooths[0]);
      }
      setNotificationCounts(countsRes.data);
      if (userBooths.length > 0) {
        const repsResults = await Promise.all(userBooths.map(b => axios.get(`${API}/reps/booth/${b.id}`).catch(() => ({ data: [] }))));
        const interactionsResults = await Promise.all(userBooths.map(b => axios.get(`${API}/interactions/booth/${b.id}`).catch(() => ({ data: [] }))));
        const allRepsData = repsResults.flatMap(r => r.data);
        const allInteractionsData = interactionsResults.flatMap(i => i.data);
        setAllReps(allRepsData);
        setAllInteractions(allInteractionsData);
        const productsRes = await axios.get(`${API}/products`).catch(() => ({ data: [] }));
        const allProductsData = productsRes.data.filter(p => userBooths.some(b => b.id === p.booth_id));
        setAllProducts(allProductsData);
        const currentBooth = activeBooth || userBooths[0];
        setReps(allRepsData.filter(r => r.booth_id === currentBooth?.id));
        setInteractions(allInteractionsData.filter(i => i.booth_id === currentBooth?.id));
        setProducts(allProductsData.filter(p => p.booth_id === currentBooth?.id));
      }
    } catch (error) { console.error('Failed to fetch data', error); toast.error('Failed to load dashboard'); }
  };

  // Filter data when active booth changes
  useEffect(() => {
    if (activeBooth) {
      setReps(allReps.filter(r => r.booth_id === activeBooth.id));
      setInteractions(allInteractions.filter(i => i.booth_id === activeBooth.id));
      setProducts(allProducts.filter(p => p.booth_id === activeBooth.id));
      const fetchBoothStats = async () => {
        try {
          const [statsRes, visitorsRes, attendeesRes, pricingRes] = await Promise.all([
            axios.get(`${API}/stats/booth/${activeBooth.id}`),
            axios.get(`${API}/stats/booth/${activeBooth.id}/visitors`).catch(() => ({ data: null })),
            axios.get(`${API}/stats/booth/${activeBooth.id}/event-attendees`).catch(() => ({ data: null })),
            axios.get(`${API}/events/${activeBooth.event_id}/list-pricing`).catch(() => ({ data: null }))
          ]);
          setCallStats(statsRes.data); setBoothVisitors(visitorsRes.data); setEventAttendees(attendeesRes.data); setListPricing(pricingRes.data);
        } catch (e) { setCallStats(null); setBoothVisitors(null); setEventAttendees(null); setListPricing(null); }
      };
      fetchBoothStats();
    }
  }, [activeBooth, allReps, allInteractions, allProducts]);

  // Download and purchase functions
  const downloadList = async (type, format = 'csv') => {
    try {
      const url = type === 'visitors' ? `${API}/stats/booth/${activeBooth.id}/visitors/download?format=${format}` : `${API}/events/${activeBooth.event_id}/download/attendee-list?format=${format}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = downloadUrl; link.download = `${type}_${activeBooth.id}.${format}`;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(downloadUrl);
      toast.success(`${type} list downloaded!`);
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to download ${type} list`); }
  };

  const handlePurchaseList = (listType) => { setPurchasingList(listType); setShowPurchaseModal(true); };
  const createListOrder = async () => { const r = await axios.post(`${API}/payments/create-list-order`, { event_id: activeBooth.event_id, list_type: purchasingList }); return r.data.order_id; };
  const captureListOrder = async (orderId) => {
    const r = await axios.post(`${API}/payments/capture-list-order/${orderId}`);
    if (r.data.status === 'success') { toast.success('List purchased!'); setShowPurchaseModal(false); setPurchasingList(null); const pricingRes = await axios.get(`${API}/events/${activeBooth.event_id}/list-pricing`); setListPricing(pricingRes.data); }
  };

  // Registration and payment
  const registerForEvent = async () => {
    if (!selectedEvent) { toast.error('Please select an event'); return; }
    if (myBooths.find(b => b.event_id === selectedEvent.id)) { toast.error('You already have a booth for this event'); return; }
    const existing = myBooths.length > 0 ? myBooths[0] : null;
    setBoothForm({ company_name: existing?.company_name || user?.company_info?.company_name || user?.company || '', description: existing?.description || user?.company_info?.description || '', slogan: existing?.slogan || '', logo_url: '', banner_url: '' });
    setShowEventModal(false); setShowBoothModal(true);
  };

  const createBooth = async (e) => {
    e.preventDefault();
    const boothData = { event_id: selectedEvent.id, tier: selectedTier, company_name: boothForm.company_name || user?.company || 'My Company', description: boothForm.description, description_translations: boothForm.description_translations, slogan_translations: boothForm.slogan_translations, logo_url: boothForm.logo_url, banner_url: boothForm.banner_url, slogan: boothForm.slogan };
    const event = events.find(e => e.id === selectedEvent.id);
    const originalPrice = event?.booth_fees?.[selectedTier] || TIER_BENEFITS[selectedTier].price;
    const finalPrice = getDiscountedAmount(originalPrice);
    if (finalPrice <= 0 && couponApplied) {
      try { await axios.post(`${API}/booths`, { ...boothData, coupon_code: couponApplied?.code || null }); toast.success('Booth created with 100% discount!'); setShowBoothModal(false); resetBoothForm(); resetCouponState(); fetchData(); return; }
      catch (error) { toast.error(error.response?.data?.detail || 'Failed to create booth'); return; }
    }
    setPendingBooth({ ...boothData, isNew: true, tier: selectedTier, price: finalPrice, originalPrice, coupon: couponApplied, event_name: selectedEvent.name });
    setShowBoothModal(false); setShowPaymentModal(true); toast.info('Please complete payment to register.');
  };

  const resetBoothForm = () => { setBoothForm({ company_name: '', description: '', description_translations: {}, slogan_translations: {}, logo_url: '', banner_url: '', slogan: '' }); setSelectedEvent(null); setSelectedTier('basic'); };
  const handlePaymentSuccess = () => { toast.success(`Payment successful! Your ${pendingBooth?.tier?.toUpperCase()} booth is now active.`); setShowPaymentModal(false); setPendingBooth(null); resetBoothForm(); resetCouponState(); fetchData(); };
  const handlePaymentError = () => { toast.error('Payment failed. Please try again.'); setShowPaymentModal(false); };
  const handlePaymentCancel = () => { toast.warning('Payment cancelled.'); setShowPaymentModal(false); setPendingBooth(null); resetBoothForm(); resetCouponState(); };

  // Coupon functions
  const validateCoupon = async () => {
    if (!couponCode.trim() || !selectedEvent) return;
    setCouponLoading(true); setCouponError('');
    try { const r = await axios.post(`${API}/events/${selectedEvent.id}/validate-coupon`, { code: couponCode.trim(), user_type: 'exhibitor' }); setCouponApplied({ ...r.data, code: couponCode.trim() }); toast.success(`Coupon applied! ${r.data.discount_percent}% discount`); }
    catch (error) { setCouponError(error.response?.data?.detail || 'Invalid coupon code'); setCouponApplied(null); }
    finally { setCouponLoading(false); }
  };
  const getDiscountedAmount = (amount) => couponApplied ? Math.max(0, amount - amount * (couponApplied.discount_percent / 100)) : amount;
  const resetCouponState = () => { setCouponCode(''); setCouponApplied(null); setCouponError(''); };

  // Payment for existing booth
  const openPaymentForBooth = (booth) => {
    const event = events.find(e => e.id === booth.event_id);
    setPendingBooth({ ...booth, price: event?.booth_fees?.[booth.tier] || TIER_BENEFITS[booth.tier]?.price || 500 });
    setShowPaymentModal(true);
  };

  // Tier upgrade
  const getTierOrder = () => ['basic', 'premium', 'elite'];
  const getUpgradeCost = (currentTier, newTier, fees) => {
    if (getTierOrder().indexOf(newTier) <= getTierOrder().indexOf(currentTier)) return 0;
    return (fees?.[newTier] || TIER_BENEFITS[newTier]?.price || 0) - (fees?.[currentTier] || TIER_BENEFITS[currentTier]?.price || 0);
  };
  const openUpgradeModal = () => { if (activeBooth) setShowUpgradeModal(true); };
  const handleTierUpgrade = (newTier) => {
    if (!activeBooth) return;
    const event = events.find(e => e.id === activeBooth.event_id);
    const cost = getUpgradeCost(activeBooth.tier, newTier, event?.booth_fees);
    if (cost <= 0) { toast.error('Cannot downgrade tier'); return; }
    setPendingBooth({ ...activeBooth, isUpgrade: true, newTier, price: cost, event_name: event?.name || 'Event' });
    setShowUpgradeModal(false); setShowPaymentModal(true); toast.info(`Please pay $${cost} to upgrade to ${newTier.toUpperCase()}`);
  };

  // Profile
  const openProfileModal = () => {
    const info = user?.company_info || {};
    setProfileForm({ name: user?.name || '', company: user?.company || info.company_name || '', phone: info.phone || '', company_email: info.company_email || '', website: info.website || '', street_address: info.street_address || '', city: info.city || '', state_province: info.state_province || '', postal_code: info.postal_code || '', country: info.country || '', slogan: activeBooth?.slogan || '', description: activeBooth?.description || '' });
    setShowProfileModal(true);
  };
  const saveProfile = async () => {
    try {
      await axios.put(`${API}/users/profile`, { name: profileForm.name, company: profileForm.company, company_info: { ...user?.company_info, company_name: profileForm.company, phone: profileForm.phone, company_email: profileForm.company_email, website: profileForm.website, street_address: profileForm.street_address, city: profileForm.city, state_province: profileForm.state_province, postal_code: profileForm.postal_code, country: profileForm.country } });
      if (activeBooth) await axios.put(`${API}/booths/${activeBooth.id}`, { slogan: profileForm.slogan, description: profileForm.description });
      toast.success('Profile updated'); setShowProfileModal(false); window.location.reload();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update profile'); }
  };

  // Video call management
  const getStatusBadge = (status) => {
    const colors = { pending: 'bg-slate-100 text-slate-700', in_queue: 'bg-blue-100 text-blue-700', on_call: 'bg-green-100 text-green-700', email_sent: 'bg-yellow-100 text-yellow-700', email_responded: 'bg-purple-100 text-purple-700', completed: 'bg-slate-100 text-slate-700' };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const updateInteractionStatus = async (id, status) => { try { await axios.put(`${API}/interactions/${id}/status?status=${status}`); toast.success('Status updated'); fetchData(); } catch { toast.error('Failed to update status'); } };

  const startVideoCall = async (interaction) => {
    try {
      toast.loading('Creating video room...'); const r = await axios.post(`${API}/video/create-room?interaction_id=${interaction.id}`); toast.dismiss();
      const activeEvent = events.find(e => e.id === activeBooth?.event_id);
      setActiveVideoRoom({ roomUrl: r.data.room_url, token: r.data.token, interactionId: r.data.interaction_id, buyerName: interaction.buyer_name, buyerCompany: interaction.buyer?.company || interaction.buyer_company, exhibitName: activeEvent?.name });
      setShowVideoCall(true); toast.success('Video room created!'); fetchData();
    } catch (error) { toast.dismiss(); toast.error(error.response?.data?.detail || 'Failed to create video room'); }
  };

  const joinExistingCall = async (interaction) => {
    try {
      toast.loading('Joining call...'); const r = await axios.get(`${API}/video/join/${interaction.id}`); toast.dismiss();
      const activeEvent = events.find(e => e.id === activeBooth?.event_id);
      setActiveVideoRoom({ roomUrl: r.data.room_url, token: r.data.token, interactionId: r.data.interaction_id, buyerName: interaction.buyer_name, buyerCompany: interaction.buyer?.company || interaction.buyer_company, exhibitName: activeEvent?.name });
      setShowVideoCall(true);
    } catch (error) { toast.dismiss(); toast.error(error.response?.data?.detail || 'Failed to join call'); }
  };

  const endVideoCall = async () => {
    if (!activeVideoRoom?.interactionId) return;
    try { await axios.post(`${API}/video/end/${activeVideoRoom.interactionId}`); setShowVideoCall(false); setActiveVideoRoom(null); toast.success('Call ended'); fetchData(); }
    catch (error) { console.error('Failed to end call:', error); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="seller-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1">{t('dashboard.vendor_dashboard')}</h1>
            <p className="text-slate-600">{t('dashboard.welcome')}, {user?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={openProfileModal}><Edit className="mr-2 h-4 w-4" /> {t('dashboard.edit_profile')}</Button>
            <Button onClick={() => setShowEventModal(true)} data-testid="register-event-btn"><Calendar className="mr-2 h-4 w-4" /> {t('dashboard.register')}</Button>
          </div>
        </div>

        {/* No Booth State */}
        {myBooths.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Store className="h-16 w-16 text-slate-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No Active Booth</h2>
              <p className="text-slate-600 mb-6">Register for an exhibit to create your virtual booth.</p>
              {liveEvents.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto"><AlertCircle className="h-6 w-6 text-amber-600 mx-auto mb-2" /><p className="text-amber-800 text-sm">No exhibits accepting registrations at the moment.</p></div>
              ) : (
                <Button onClick={() => setShowEventModal(true)}><Plus className="mr-2 h-4 w-4" /> Register for Exhibit</Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Exhibit Selector */}
            {myBooths.length > 1 && (
              <Card className="mb-6 border-primary/20 bg-primary/5">
                <CardContent className="py-4">
                  <div className="flex items-center gap-2 mb-3"><Store className="h-5 w-5 text-primary" /><h3 className="font-semibold text-slate-900">Your Exhibits</h3><span className="text-sm text-slate-500">({myBooths.length} active)</span></div>
                  <div className="flex flex-wrap gap-2">
                    {myBooths.map((booth) => {
                      const event = events.find(e => e.id === booth.event_id);
                      const isActive = activeBooth?.id === booth.id;
                      return (
                        <Button key={booth.id} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveBooth(booth)}
                          className={`flex items-center gap-2 ${isActive ? 'ring-2 ring-primary ring-offset-2' : ''}`}>
                          <span className={`w-2 h-2 rounded-full ${event?.status === 'live' ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`}></span>
                          <span className="font-medium">{event?.name || 'Unknown Exhibit'}</span>
                          <Badge className={`${TIER_BENEFITS[booth.tier]?.color} text-xs`}>{booth.tier?.toUpperCase()}</Badge>
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-primary/30"><CardContent className="pt-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600">Active Exhibit</p><p className="text-lg font-semibold">{events.find(e => e.id === activeBooth?.event_id)?.name || 'N/A'}</p></div><Calendar className="h-8 w-8 text-primary" /></div></CardContent></Card>
              <Card><CardContent className="pt-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600">Booth Tier</p><Badge className={TIER_BENEFITS[activeBooth?.tier || myBooths[0]?.tier]?.color}>{(activeBooth?.tier || myBooths[0]?.tier)?.toUpperCase()}</Badge></div><Store className="h-8 w-8 text-accent" /></div></CardContent></Card>
              <Card><CardContent className="pt-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600">Representatives</p><p className="text-lg font-semibold">{reps.length}</p></div><Users className="h-8 w-8 text-primary" /></div></CardContent></Card>
              <Card><CardContent className="pt-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600">Attendee Interactions</p><p className="text-lg font-semibold">{interactions.length}</p></div><MessageSquare className="h-8 w-8 text-green-600" /></div></CardContent></Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-7 max-w-3xl">
                <TabsTrigger value="overview">{t('dashboard.overview')}</TabsTrigger>
                <TabsTrigger value="media">{t('dashboard.media')}</TabsTrigger>
                <TabsTrigger value="reps">{t('dashboard.reps')}</TabsTrigger>
                <TabsTrigger value="products">{t('dashboard.products')}</TabsTrigger>
                <TabsTrigger value="messages" className="relative">
                  {t('dashboard.messages')}
                  {notificationCounts.messages > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{notificationCounts.messages > 9 ? '9+' : notificationCounts.messages}</span>}
                </TabsTrigger>
                <TabsTrigger value="interactions" className="relative">
                  {t('dashboard.video_conference')}
                  {notificationCounts.queue > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{notificationCounts.queue > 9 ? '9+' : notificationCounts.queue}</span>}
                </TabsTrigger>
                <TabsTrigger value="stats"><BarChart3 className="h-4 w-4 mr-1" /> {t('dashboard.stats')}</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <SellerOverviewTab activeBooth={activeBooth} events={events} TIER_BENEFITS={TIER_BENEFITS} openUpgradeModal={openUpgradeModal} openPaymentForBooth={openPaymentForBooth} />
              </TabsContent>
              <TabsContent value="media">
                <SellerMediaTab activeBooth={activeBooth} setActiveBooth={setActiveBooth} fetchData={fetchData} />
              </TabsContent>
              <TabsContent value="reps">
                <SellerRepsTab reps={reps} activeBooth={activeBooth} fetchData={fetchData} />
              </TabsContent>
              <TabsContent value="products">
                <SellerProductsTab products={products} selectedBooth={activeBooth} myBooths={myBooths} fetchData={fetchData} />
              </TabsContent>
              <TabsContent value="messages">
                <Card><CardHeader><CardTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5" />{t('dashboard.messages_title')}</CardTitle><CardDescription>{t('dashboard.messages_desc')}</CardDescription></CardHeader>
                  <CardContent>{myBooths.length === 0 ? <div className="text-center py-8"><MessageSquare className="h-12 w-12 text-slate-400 mx-auto mb-4" /><p className="text-slate-600 mb-4">Register for an event to start receiving messages</p></div> : <MessagingPanel userType="seller" />}</CardContent></Card>
              </TabsContent>
              <TabsContent value="interactions">
                <SellerInteractionsTab interactions={interactions} getStatusBadge={getStatusBadge} startVideoCall={startVideoCall} joinExistingCall={joinExistingCall} endVideoCall={endVideoCall} updateInteractionStatus={updateInteractionStatus} />
              </TabsContent>
              <TabsContent value="stats">
                <SellerStatsTab callStats={callStats} boothVisitors={boothVisitors} eventAttendees={eventAttendees} listPricing={listPricing} activeBooth={activeBooth} downloadList={downloadList} handlePurchaseList={handlePurchaseList} openUpgradeModal={openUpgradeModal} />
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Event Selection Modal */}
        <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Register for Live Event</DialogTitle><DialogDescription>Select an event to register as an exhibitor.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              {liveEvents.length === 0 ? (
                <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-lg"><AlertCircle className="h-10 w-10 text-amber-600 mx-auto mb-3" /><p className="text-amber-800 font-medium">No Live Events Available</p></div>
              ) : (
                <>
                  <div><Label>Select Event</Label>
                    <Select onValueChange={(val) => setSelectedEvent(liveEvents.find(e => e.id === val))}><SelectTrigger><SelectValue placeholder="Choose an event" /></SelectTrigger>
                      <SelectContent>{liveEvents.map((event) => (<SelectItem key={event.id} value={event.id}><span className="flex items-center"><span className={`w-2 h-2 rounded-full mr-2 ${event.status === 'live' ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`}></span>{event.name} ({event.status === 'live' ? 'LIVE' : 'Registration Open'})</span></SelectItem>))}</SelectContent></Select></div>
                  <div><Label>Select Tier</Label>
                    <div className="grid grid-cols-3 gap-4 mt-2">
                      {Object.entries(TIER_BENEFITS).map(([key, tier]) => {
                        const eventPrice = selectedEvent?.booth_fees?.[key] || tier.price;
                        return (<div key={key} onClick={() => setSelectedTier(key)} className={`p-4 border rounded-lg cursor-pointer transition-all ${selectedTier === key ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200 hover:border-slate-300'}`}>
                          <h4 className="font-semibold">{t(tier.nameKey)}</h4><p className="text-2xl font-bold text-primary">${eventPrice}</p>
                          <ul className="mt-2 text-xs text-slate-600 space-y-1">{tier.benefitKeys.map((k, i) => <li key={i}>• {t(k)}</li>)}</ul></div>);
                      })}
                    </div></div>
                  <Button onClick={registerForEvent} className="w-full" disabled={!selectedEvent}>Continue to Booth Setup</Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Booth Setup Modal */}
        <Dialog open={showBoothModal} onOpenChange={(open) => { setShowBoothModal(open); if (!open) resetCouponState(); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{t('dashboard.setup_your_booth')}</DialogTitle><DialogDescription>{t('dashboard.configure_booth_for')} {selectedEvent?.name}</DialogDescription></DialogHeader>
            <form onSubmit={createBooth} className="space-y-4">
              <div><Label>{t('dashboard.company_name')}</Label><Input value={boothForm.company_name} onChange={(e) => setBoothForm({ ...boothForm, company_name: e.target.value })} placeholder={user?.company || t('dashboard.your_company_name')} /></div>
              <MultiLanguageInput label={t('dashboard.booth_description')} defaultValue={boothForm.description} translations={boothForm.description_translations}
                onDefaultChange={(v) => setBoothForm({ ...boothForm, description: v })} onTranslationsChange={(t) => setBoothForm({ ...boothForm, description_translations: t })} placeholder={t('dashboard.describe_company')} multiline required />
              <MultiLanguageInput label={t('dashboard.slogan_optional')} defaultValue={boothForm.slogan} translations={boothForm.slogan_translations}
                onDefaultChange={(v) => setBoothForm({ ...boothForm, slogan: v })} onTranslationsChange={(t) => setBoothForm({ ...boothForm, slogan_translations: t })} placeholder={t('dashboard.company_tagline')} />
              <div className="bg-slate-50 p-3 rounded-md">
                <p className="text-sm"><strong>{t('dashboard.tier')}:</strong> {t(TIER_BENEFITS[selectedTier]?.nameKey)}</p>
                <p className="text-sm"><strong>{t('dashboard.price')}:</strong> ${selectedEvent?.booth_fees?.[selectedTier] || TIER_BENEFITS[selectedTier]?.price}</p>
              </div>
              {selectedEvent?.coupon_enabled && (selectedEvent?.booth_fees?.[selectedTier] || TIER_BENEFITS[selectedTier]?.price) > 0 && (
                <div className="border rounded-lg p-4 space-y-3" data-testid="seller-coupon-section">
                  <Label className="text-sm font-medium">Have a coupon code?</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Enter coupon code" value={couponCode} onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }} disabled={!!couponApplied} className="flex-1" data-testid="seller-coupon-input" />
                    {couponApplied ? <Button type="button" variant="outline" onClick={() => { setCouponApplied(null); setCouponCode(''); }} data-testid="seller-coupon-remove-btn">Remove</Button>
                      : <Button type="button" onClick={validateCoupon} disabled={!couponCode.trim() || couponLoading} data-testid="seller-coupon-apply-btn">{couponLoading ? 'Checking...' : 'Apply'}</Button>}
                  </div>
                  {couponError && <p className="text-sm text-red-500" data-testid="seller-coupon-error">{couponError}</p>}
                  {couponApplied && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="seller-coupon-applied">
                      <p className="text-sm text-green-700 font-medium">Coupon applied! {couponApplied.discount_percent}% discount</p>
                      <div className="mt-2 text-sm">
                        <p className="text-slate-600">Original: {formatPrice(selectedEvent?.booth_fees?.[selectedTier] || TIER_BENEFITS[selectedTier]?.price)}</p>
                        <p className="text-slate-600">Discount: -{formatPrice((selectedEvent?.booth_fees?.[selectedTier] || TIER_BENEFITS[selectedTier]?.price) * (couponApplied.discount_percent / 100))}</p>
                        <p className="font-semibold text-green-700">Total: {formatPrice(getDiscountedAmount(selectedEvent?.booth_fees?.[selectedTier] || TIER_BENEFITS[selectedTier]?.price))}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <Button type="submit" className="w-full" data-testid="seller-create-booth-btn">{t('dashboard.create_booth_payment')}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment Modal */}
        <Dialog open={showPaymentModal} onOpenChange={(open) => { if (!open) handlePaymentCancel(); }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Complete Booth Payment</DialogTitle><DialogDescription>Pay for your {pendingBooth?.tier?.toUpperCase()} booth to activate it</DialogDescription></DialogHeader>
            {pendingBooth && (
              <>
                {pendingBooth.coupon && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4" data-testid="seller-payment-coupon-info">
                    <p className="text-sm text-green-700 font-medium">Coupon "{pendingBooth.coupon.code}" applied</p>
                    <div className="mt-2 text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-slate-600">Original:</span><span>{formatPrice(pendingBooth.originalPrice)}</span></div>
                      <div className="flex justify-between text-green-600"><span>Discount ({pendingBooth.coupon.discount_percent}%):</span><span>-{formatPrice(pendingBooth.originalPrice - pendingBooth.price)}</span></div>
                      <div className="flex justify-between font-semibold border-t pt-1 mt-1"><span>Total:</span><span>{formatPrice(pendingBooth.price)}</span></div>
                    </div>
                  </div>
                )}
                <PayPalPayment amount={pendingBooth.price}
                  paymentType={pendingBooth.isUpgrade ? "booth_upgrade" : "booth_fee"}
                  referenceId={pendingBooth.isNew ? null : pendingBooth.id}
                  couponCode={pendingBooth.coupon?.code || null}
                  pendingBoothData={pendingBooth.isNew ? { event_id: pendingBooth.event_id, company_name: pendingBooth.company_name, description: pendingBooth.description, tier: pendingBooth.tier, logo_url: pendingBooth.logo_url, banner_url: pendingBooth.banner_url, slogan: pendingBooth.slogan }
                    : pendingBooth.isUpgrade ? { isUpgrade: true, newTier: pendingBooth.newTier, booth_id: pendingBooth.id } : null}
                  description={pendingBooth.isUpgrade ? `Upgrade to ${pendingBooth.newTier?.toUpperCase()} - ${pendingBooth.company_name}` : `${pendingBooth.tier?.toUpperCase()} Booth Fee - ${pendingBooth.company_name}${pendingBooth.coupon ? ` (Coupon: ${pendingBooth.coupon.code})` : ''}`}
                  onSuccess={handlePaymentSuccess} onError={handlePaymentError} onCancel={handlePaymentCancel} />
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Upgrade Modal */}
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Upgrade Your Tier</DialogTitle><DialogDescription>Upgrade to unlock more benefits for {events.find(e => e.id === activeBooth?.event_id)?.name}</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg mb-4"><p className="text-sm"><strong>Current Tier:</strong> <Badge className={TIER_BENEFITS[activeBooth?.tier]?.color}>{activeBooth?.tier?.toUpperCase()}</Badge></p></div>
              {getTierOrder().filter(tier => getTierOrder().indexOf(tier) > getTierOrder().indexOf(activeBooth?.tier)).map(tier => {
                const event = events.find(e => e.id === activeBooth?.event_id);
                const cost = getUpgradeCost(activeBooth?.tier, tier, event?.booth_fees);
                return (<div key={tier} className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary`} onClick={() => handleTierUpgrade(tier)}>
                  <div className="flex justify-between items-start"><div><h4 className="font-semibold text-lg">{t(TIER_BENEFITS[tier]?.nameKey)}</h4><ul className="mt-2 text-sm text-slate-600 space-y-1">{TIER_BENEFITS[tier]?.benefitKeys?.map((k, i) => <li key={i}>• {t(k)}</li>)}</ul></div>
                    <div className="text-right"><p className="text-2xl font-bold text-primary">+${cost}</p><p className="text-xs text-slate-500">{t('dashboard.upgrade_cost')}</p></div></div></div>);
              })}
              <Button variant="outline" className="w-full" onClick={() => setShowUpgradeModal(false)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Profile Modal */}
        <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Profile</DialogTitle><DialogDescription>Update your personal and company information</DialogDescription></DialogHeader>
            <div className="space-y-6">
              <div><h4 className="font-medium text-sm text-slate-700 mb-3">Personal Information</h4>
                <div className="grid grid-cols-2 gap-4"><div><Label>Full Name</Label><Input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} /></div>
                  <div><Label>Phone Number</Label><Input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} /></div></div></div>
              <div><h4 className="font-medium text-sm text-slate-700 mb-3">{t('dashboard.company_information')}</h4>
                <div className="grid grid-cols-2 gap-4"><div><Label>{t('dashboard.company_name')}</Label><Input value={profileForm.company} onChange={(e) => setProfileForm({ ...profileForm, company: e.target.value })} /></div>
                  <div><Label>{t('dashboard.company_email')}</Label><Input type="email" value={profileForm.company_email} onChange={(e) => setProfileForm({ ...profileForm, company_email: e.target.value })} /></div>
                  <div className="col-span-2"><Label>{t('dashboard.website')}</Label><Input value={profileForm.website} onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })} /></div></div></div>
              {activeBooth && (<div><h4 className="font-medium text-sm text-slate-700 mb-3">{t('dashboard.booth_info')}</h4>
                <div className="space-y-4"><div><Label>{t('dashboard.slogan')}</Label><Input value={profileForm.slogan} onChange={(e) => setProfileForm({ ...profileForm, slogan: e.target.value })} /></div>
                  <div><Label>{t('dashboard.description')}</Label><Textarea value={profileForm.description} onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })} rows={4} /></div></div></div>)}
              <div><h4 className="font-medium text-sm text-slate-700 mb-3">Address</h4>
                <div className="space-y-4"><div><Label>Street Address</Label><Input value={profileForm.street_address} onChange={(e) => setProfileForm({ ...profileForm, street_address: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-4"><div><Label>City</Label><Input value={profileForm.city} onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })} /></div>
                    <div><Label>State/Province</Label><Input value={profileForm.state_province} onChange={(e) => setProfileForm({ ...profileForm, state_province: e.target.value })} /></div></div>
                  <div className="grid grid-cols-2 gap-4"><div><Label>Postal Code</Label><Input value={profileForm.postal_code} onChange={(e) => setProfileForm({ ...profileForm, postal_code: e.target.value })} /></div>
                    <div><Label>Country</Label><Input value={profileForm.country} onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })} /></div></div></div></div>
              <div className="flex gap-2 pt-4 border-t"><Button variant="outline" className="flex-1" onClick={() => setShowProfileModal(false)}>Cancel</Button><Button className="flex-1" onClick={saveProfile}>Save Changes</Button></div>
            </div>
          </DialogContent>
        </Dialog>

        {/* List Purchase Modal */}
        <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
          <DialogContent>
            <DialogHeader><DialogTitle>Purchase {purchasingList === 'attendee_list' ? 'Attendee' : 'Exhibitor'} List</DialogTitle>
              <DialogDescription>Get access to download the complete list for this event.</DialogDescription></DialogHeader>
            <div className="py-4">
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-600">Event: <span className="font-medium">{listPricing?.event_name}</span></p>
                <p className="text-lg font-bold mt-2">Price: ${purchasingList === 'attendee_list' ? listPricing?.attendee_list_price : listPricing?.exhibitor_list_price}</p>
              </div>
              <PayPalPayment amount={purchasingList === 'attendee_list' ? listPricing?.attendee_list_price : listPricing?.exhibitor_list_price}
                onApprove={async (data) => { await captureListOrder(data.orderID); }} createOrder={createListOrder} />
            </div>
          </DialogContent>
        </Dialog>

        {/* Video Call */}
        {showVideoCall && activeVideoRoom && (
          <VideoCall key={activeVideoRoom.interactionId} roomUrl={activeVideoRoom.roomUrl} token={activeVideoRoom.token} userName={user?.name || 'Exhibitor'}
            onCallEnd={endVideoCall} onError={(error) => { console.error('Video call error:', error); toast.error('Video call error'); }} />
        )}
      </div>
    </div>
  );
};

export default SellerDashboard;
